import lodash from 'lodash'

import * as helpers from './helpers'
import type {Day, Month, Calendar} from './types'

const DAYS_IN_WEEK = 7
const NOON = 12

export function createDayInstance(date: Date, outside: boolean): Day {
  const formatted = date.getDate()
  const toJSON = () => helpers.formatDate(date)
  return {date, outside, formatted, toJSON}
}

export function isLastDayOfWeek(day: Day) {
  const tomorrow = helpers.addDays(day.date, 1)
  return tomorrow.getDay() === helpers.getFirstDayOfWeek()
}

export function daysFromPrevMonth(incompleteWeek: Array<Day>): Array<Day> {
  return lodash
    .range(DAYS_IN_WEEK - incompleteWeek.length, 0, -1)
    .map((index) =>
      createDayInstance(
        helpers.subtractDays(incompleteWeek[0].date, index),
        true
      )
    )
}

export function daysFromNextMonth(incompleteWeek): Array<Day> {
  return lodash
    .range(DAYS_IN_WEEK - incompleteWeek.length)
    .map((index) =>
      createDayInstance(
        helpers.addDays(
          incompleteWeek[incompleteWeek.length - 1].date,
          index + 1
        ),
        true
      )
    )
}

export function daysInMonth(start: Date): Array<Day> {
  const daysCount = helpers.getDaysCountForCurrentMonth(start)

  return lodash.range(1, daysCount + 1).map((day) => {
    const date = new Date(start.getFullYear(), start.getMonth(), day, NOON)
    return createDayInstance(date, false)
  })
}

export function createMonth(start: Date, index: number, outOf: number): Month {
  const days = daysInMonth(start)

  const accumulator = days.reduce(
    // @ts-ignore type acc
    (acc, day: Day) => {
      const {weeks, current} = acc
      const newCurrent = [...current, day]

      if (newCurrent.length > 0 && isLastDayOfWeek(day)) {
        // beginning next week update weeks accumulator
        if (weeks.length === 0 && newCurrent.length < 7) {
          // week not complete, add days from previous month
          return {
            weeks: [
              ...weeks,
              [...daysFromPrevMonth(newCurrent), ...newCurrent],
            ],
            current: [],
          }
        }
        return {weeks: [...weeks, newCurrent], current: []}
      }

      if (days.indexOf(day) === days.length - 1) {
        if (newCurrent.length < 7) {
          // week not complete, add days from next month
          return {
            weeks: [
              ...weeks,
              [...newCurrent, ...daysFromNextMonth(newCurrent)],
            ],
            current: [],
          }
        }
        return {weeks: [...weeks, newCurrent], current: []}
      }

      return {weeks, current: newCurrent}
    },
    {weeks: [], current: []}
  )

  const title = helpers.formatMonthTitle(start)
  const hasArrowNext = index === outOf - 1
  const hasArrowPrev = index === 0

  return {
    title,
    // @ts-ignore once acc typed it will be okay
    weeks: accumulator.weeks,
    hasArrowNext,
    hasArrowPrev,
  }
}

function _createCalendar(start: Date, months: number): Calendar {
  return {
    weekdayHeaders: helpers.getArrayOfWeekdayHeaders(),
    months: lodash.range(months).map((index) => {
      const newStart = helpers.addMonths(start, index)
      return createMonth(newStart, index, months)
    }),
  }
}

export const createCalendar = lodash.memoize(
  _createCalendar,
  (start, months) => Number(start) + months
)
